import { Component } from '@theme/component';

/**
 * Progressive catalog filters. Native sorting remains available without JavaScript.
 * @extends {Component}
 */
class LevayoCatalog extends Component {
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;
    document.addEventListener('pointerdown', this.#dismissFilters, { signal });
    document.addEventListener('focusin', this.#dismissFilters, { signal });
    this.addEventListener('keydown', this.#handleEscape, { signal });
    const params = new URL(window.location.href).searchParams;
    if (this.refs.search instanceof HTMLInputElement) this.refs.search.value = params.get('lv_q') || '';
    if (this.refs.deals instanceof HTMLInputElement) this.refs.deals.checked = params.get('lv_deals') === '1';
    if (this.refs.available instanceof HTMLInputElement) this.refs.available.checked = params.get('lv_available') === '1';
    this.setAttribute('enhanced', '');
    this.filterCards();
  }

  updatedCallback() {
    super.updatedCallback();
    this.filterCards();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
  }

  #dismissFilters = (event) => {
    const filters = this.refs.filters;
    if (filters instanceof HTMLDetailsElement && filters.open && !filters.contains(event.target)) {
      filters.open = false;
    }
  };

  #handleEscape = (event) => {
    if (event.key === 'Escape' && this.refs.filters instanceof HTMLDetailsElement && this.refs.filters.open) {
      event.preventDefault();
      this.closeFilters();
    }
  };

  closeFilters() {
    if (this.refs.filters instanceof HTMLDetailsElement) this.refs.filters.open = false;
    if (this.refs.filterToggle instanceof HTMLElement) this.refs.filterToggle.focus();
  }

  /** Filter only the rendered cards, without changing product or cart state. */
  filterCards() {
    const search = this.refs.search;
    const deals = this.refs.deals;
    const available = this.refs.available;
    const cards = Array.isArray(this.refs.cards) ? this.refs.cards : [];

    const normalize = (value) => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase();
    const query = search instanceof HTMLInputElement ? normalize(search.value.trim()) : '';
    const terms = query.split(/\s+/).filter(Boolean);
    const onlyDeals = deals instanceof HTMLInputElement && deals.checked;
    const onlyAvailable = available instanceof HTMLInputElement && available.checked;
    let count = 0;

    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;
      const text = normalize(card.dataset.search || '');
      const matches = terms.every((term) => text.includes(term)) &&
        (!onlyDeals || card.dataset.deal === 'true') &&
        (!onlyAvailable || card.dataset.available === 'true');
      card.hidden = !matches;
      if (matches) count += 1;
    }

    if (this.refs.count instanceof HTMLElement) {
      this.refs.count.textContent = count === 1 && this.dataset.countOne ? this.dataset.countOne :
        (this.dataset.countLabel || '__COUNT__').replace('__COUNT__', String(count));
    }
    if (this.refs.empty instanceof HTMLElement) this.refs.empty.hidden = count > 0;
    if (this.refs.reset instanceof HTMLElement) this.refs.reset.hidden = !query && !onlyDeals && !onlyAvailable;
    if (this.refs.filterCount instanceof HTMLElement) {
      const activeFilters = Number(onlyDeals) + Number(onlyAvailable);
      this.refs.filterCount.textContent = String(activeFilters);
      this.refs.filterCount.hidden = activeFilters === 0;
    }
  }

  /** Reset all local controls and restore the catalog. */
  resetFilters() {
    if (this.refs.search instanceof HTMLInputElement) this.refs.search.value = '';
    if (this.refs.deals instanceof HTMLInputElement) this.refs.deals.checked = false;
    if (this.refs.available instanceof HTMLInputElement) this.refs.available.checked = false;
    this.filterCards();
    if (this.refs.search instanceof HTMLInputElement) this.refs.search.focus();
    else this.closeFilters();
  }

  /** @param {Event} event - Native sort selector change. */
  submitSort(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', select.value);
    url.searchParams.delete('page');
    const filters = {
      lv_q: this.refs.search instanceof HTMLInputElement ? this.refs.search.value.trim() : '',
      lv_deals: this.refs.deals instanceof HTMLInputElement && this.refs.deals.checked ? '1' : '',
      lv_available: this.refs.available instanceof HTMLInputElement && this.refs.available.checked ? '1' : '',
    };
    for (const [name, value] of Object.entries(filters)) {
      if (value) url.searchParams.set(name, value);
      else url.searchParams.delete(name);
    }
    url.hash = this.closest('section')?.id || this.closest('.lvc-products')?.id || '';
    window.location.assign(url.href);
  }
}

if (!customElements.get('levayo-catalog')) customElements.define('levayo-catalog', LevayoCatalog);
